import { useTranslation } from 'react-i18next'
import { Card, CardBody, Chip } from '@structural-climbing/ui'
import { formatMoney } from './money'
import { ProductImage } from './ProductImage'
import type { Product } from './types'

interface Props {
  product: Product
  currency: string
  /** How many the active cart already holds. */
  quantityInCart: number
  onAdd: (product: Product) => void
}

/** One tap adds one. The whole tile is the target, sized for a thumb. */
export function ProductTile({ product, currency, quantityInCart, onAdd }: Props) {
  const { t } = useTranslation()
  const out = product.stock <= 0
  const low = !out && product.stock <= 5

  return (
    <Card
      isPressable
      shadow="sm"
      onPress={() => onAdd(product)}
      className="relative w-full text-left"
      aria-label={`${product.name}, ${formatMoney(product.sell_price, currency)}`}
    >
      {quantityInCart > 0 && (
        <Chip
          color="primary"
          size="sm"
          className="absolute right-2 top-2 z-10"
          aria-label={t('products.inCart', { n: quantityInCart })}
        >
          {quantityInCart}
        </Chip>
      )}
      <ProductImage
        name={product.name}
        imageUrl={product.image_url}
        className="aspect-square w-full"
      />
      <CardBody className="gap-1 p-3">
        <p className="line-clamp-2 text-sm font-medium leading-snug">{product.name}</p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">{formatMoney(product.sell_price, currency)}</p>
          {/* Says what a tap does. The whole tile is still the target. */}
          <Chip
            aria-hidden="true"
            color="primary"
            radius="full"
            classNames={{
              base: 'h-9 w-9 min-w-9 max-w-9 shrink-0 justify-center p-0',
              content: 'flex items-center justify-center p-0',
            }}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" strokeWidth={2.5} strokeLinecap="round" />
            </svg>
          </Chip>
        </div>
        {/* Stock never blocks a sale. It only says what the count is. */}
        <p
          className={`text-xs ${out ? 'text-danger' : low ? 'text-warning-600' : 'text-foreground/50'}`}
        >
          {out ? t('products.outOfStock') : t('products.stockLeft', { n: product.stock })}
        </p>
      </CardBody>
    </Card>
  )
}
